import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/i18n/LanguageContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ShoppingCart, Loader2, Mail, Clock, Package } from 'lucide-react';

import { API } from '@/lib/apiBase';

const formatTimeAgo = (iso, t) => {
  if (!iso) return '';
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 60) return t('abandonedCarts.minutesAgo').replace('{n}', mins);
  const hours = Math.floor(mins / 60);
  if (hours < 24) return t('abandonedCarts.hoursAgo').replace('{n}', hours);
  const days = Math.floor(hours / 24);
  return t('abandonedCarts.daysAgo').replace('{n}', days);
};

export default function AdminAbandonedCarts() {
  const { getAuthHeaders } = useAuth();
  const { t, language } = useLanguage();
  const [carts, setCarts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hours, setHours] = useState('24');
  const [sendingTo, setSendingTo] = useState(null);

  const fetchCarts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/abandoned-carts?hours=${hours}`, {
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed');
      setCarts(await res.json());
    } catch {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  }, [hours, getAuthHeaders, t]);

  useEffect(() => { fetchCarts(); }, [fetchCarts]);

  const sendReminder = async (userId) => {
    setSendingTo(userId);
    try {
      const res = await fetch(
        `${API}/admin/abandoned-carts/${userId}/remind?lang=${language}`,
        { method: 'POST', headers: getAuthHeaders(), credentials: 'include' }
      );
      if (!res.ok) throw new Error('Failed');
      toast.success(t('abandonedCarts.reminderSent'));
      fetchCarts();
    } catch {
      toast.error(t('common.error'));
    } finally {
      setSendingTo(null);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-12 py-12" data-testid="admin-abandoned-carts">
      <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight mb-2">
            {t('abandonedCarts.title')}
          </h1>
          <p className="text-muted-foreground">{t('abandonedCarts.description')}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground whitespace-nowrap">
            {t('abandonedCarts.olderThan')}
          </span>
          <Select value={hours} onValueChange={setHours}>
            <SelectTrigger className="w-[160px] bg-background border-border" data-testid="abandoned-hours-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-card border-border">
              <SelectItem value="1">{t('abandonedCarts.h1')}</SelectItem>
              <SelectItem value="6">{t('abandonedCarts.h6')}</SelectItem>
              <SelectItem value="24">{t('abandonedCarts.h24')}</SelectItem>
              <SelectItem value="72">{t('abandonedCarts.h72')}</SelectItem>
              <SelectItem value="168">{t('abandonedCarts.h168')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : carts.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
            <ShoppingCart className="w-10 h-10 text-muted-foreground" />
          </div>
          <h3 className="text-xl font-bold mb-2">{t('abandonedCarts.empty')}</h3>
          <p className="text-muted-foreground">{t('abandonedCarts.emptyDesc')}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {carts.map((cart) => (
            <Card key={cart.user_id} className="bg-card border-border" data-testid={`abandoned-cart-${cart.user_id}`}>
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold truncate">{cart.user_name || cart.user_email}</h3>
                      {cart.last_reminder_at && (
                        <Badge variant="outline" className="text-xs">
                          {t('abandonedCarts.reminded')}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground truncate">{cart.user_email}</p>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Package className="w-4 h-4" />
                        {cart.item_count} {t('abandonedCarts.items')}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {formatTimeAgo(cart.updated_at, t)}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="text-right">
                      <div className="text-xs text-muted-foreground">{t('abandonedCarts.total')}</div>
                      <div className="text-lg font-bold text-primary">CHF {Number(cart.total || 0).toFixed(2)}</div>
                    </div>
                    <Button
                      onClick={() => sendReminder(cart.user_id)}
                      disabled={sendingTo === cart.user_id}
                      size="sm"
                      data-testid={`remind-btn-${cart.user_id}`}
                    >
                      {sendingTo === cart.user_id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Mail className="w-4 h-4 mr-2" />
                          {t('abandonedCarts.sendReminder')}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
